import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Avatar,
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getProfile, updateProfile, updateProfilePhoto } from '../services/api';
import CropModal from './CropModal';

const PHONE_REGEX = /^\+\d{7,15}$/;
const EXTENSION_REGEX = /^\d{2,6}$/;
const LATIN_NAME_REGEX = /^[A-Za-z\s'-]+$/;

const ProfileSetupModal = ({ open, onClose, mode = 'paywall' }) => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneExtension, setPhoneExtension] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoChanged, setPhotoChanged] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      loadProfileData();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfileData = async () => {
    try {
      const response = await getProfile();
      const profile = response.data;
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setPhone(profile.phone || '');
      setPhoneExtension(profile.phone_extension || '');
      if (profile.photo_base64) {
        setPhotoPreview(profile.photo_base64);
      } else if (profile.google_picture_url) {
        setPhotoPreview(profile.google_picture_url);
      }
    } catch (error) {
      // Fallback to user data from context
      setFirstName(user?.first_name || '');
      setLastName(user?.last_name || '');
      setPhone(user?.phone || '');
      setPhoneExtension(user?.phone_extension || '');
      if (user?.google_picture_url) {
        setPhotoPreview(user.google_picture_url);
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!firstName.trim()) {
      newErrors.firstName = t('profile.required', 'This field is required');
    } else if (!LATIN_NAME_REGEX.test(firstName.trim())) {
      newErrors.firstName = t('profile.latinOnly', 'Latin characters only');
    }
    if (!lastName.trim()) {
      newErrors.lastName = t('profile.required', 'This field is required');
    } else if (!LATIN_NAME_REGEX.test(lastName.trim())) {
      newErrors.lastName = t('profile.latinOnly', 'Latin characters only');
    }
    if (!phone.trim()) {
      newErrors.phone = t('profile.required', 'This field is required');
    } else if (!PHONE_REGEX.test(phone.trim())) {
      newErrors.phone = t('profile.invalidPhone', 'Phone must start with + and contain 7-15 digits');
    }
    if (!phoneExtension.trim()) {
      newErrors.phoneExtension = t('profile.required', 'This field is required');
    } else if (!EXTENSION_REGEX.test(phoneExtension.trim())) {
      newErrors.phoneExtension = t('profile.invalidExtension', 'Extension must be 2-6 digits');
    }
    if (!photoPreview || photoPreview === user?.google_picture_url) {
      // Photo is required only if there's no existing uploaded photo
      if (!photoPreview?.startsWith('data:image/')) {
        newErrors.photo = t('profile.photoRequired', 'Please upload a profile photo');
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // True only when the only photo we have is the user's Google avatar URL —
  // backend requires an explicit base64 upload, so this state needs a visible
  // hint, not just a disabled button.
  const needsPhotoUpload = !photoPreview || !photoPreview.startsWith('data:image/');

  const isFormValid = () => {
    return (
      firstName.trim() &&
      LATIN_NAME_REGEX.test(firstName.trim()) &&
      lastName.trim() &&
      LATIN_NAME_REGEX.test(lastName.trim()) &&
      PHONE_REGEX.test(phone.trim()) &&
      EXTENSION_REGEX.test(phoneExtension.trim()) &&
      photoPreview?.startsWith('data:image/')
    );
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setRawImage(reader.result);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset file input so same file can be selected again
    event.target.value = '';
  };

  const handleCropComplete = (croppedBase64) => {
    setPhotoPreview(croppedBase64);
    setPhotoChanged(true);
    setErrors((prev) => ({ ...prev, photo: undefined }));
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setErrors((prev) => ({ ...prev, submit: undefined }));
    try {
      // Upload photo first if changed
      if (photoChanged && photoPreview) {
        await updateProfilePhoto(photoPreview);
      }

      // Update profile fields
      const response = await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        phone_extension: phoneExtension.trim(),
      });

      // Refresh user data in context
      const profileResponse = await getProfile();
      updateUser({
        ...user,
        ...profileResponse.data,
        profile_completed: response.data.profile_completed,
      });

      if (onClose) onClose();
    } catch (error) {
      console.error('Failed to save profile:', error);
      // Surface the actual reason instead of silently swallowing it; users
      // were stuck staring at the modal after the photo endpoint returned
      // 400 (e.g. "Photo too large. Maximum size is 500KB").
      const backendMessage = error?.response?.data?.error;
      setErrors((prev) => ({
        ...prev,
        submit: backendMessage || t('profile.saveFailed', 'Could not save your profile. Please try again or contact support.'),
      }));
    } finally {
      setSaving(false);
    }
  };

  const isPaywall = mode === 'paywall';
  const initials = `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();

  return (
    <>
      <Dialog
        open={open}
        onClose={isPaywall ? undefined : onClose}
        disableEscapeKeyDown={isPaywall}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span">
            {isPaywall
              ? t('profile.title', 'Complete Your Profile')
              : t('profile.editTitle', 'Edit Profile')}
          </Typography>
          {!isPaywall && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent>
          {isPaywall && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('profile.subtitle', 'Please fill in your details to continue using the calculator')}
            </Typography>
          )}

          {/* Photo section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={photoPreview || undefined}
              sx={{
                width: 120,
                height: 120,
                fontSize: 40,
                bgcolor: '#00C853',
                mb: 1.5,
              }}
            >
              {!photoPreview && initials}
            </Avatar>
            <Button
              variant={needsPhotoUpload ? 'contained' : 'outlined'}
              color={needsPhotoUpload ? 'warning' : 'primary'}
              size="small"
              startIcon={<PhotoCameraIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              {photoPreview?.startsWith('data:image/')
                ? t('profile.changePhoto', 'Change Photo')
                : t('profile.uploadPhotoFromComputer', 'Upload Photo from your computer')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileSelect}
            />
            {errors.photo && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.photo}
              </Typography>
            )}
          </Box>

          {/* Persistent warning shown until the user uploads a real photo —
              users were stuck because the Google avatar looked valid but the
              backend silently rejected it. */}
          {needsPhotoUpload && (
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('profile.photoUploadRequired', 'Upload your own photo')}
              </AlertTitle>
              {t(
                'profile.photoUploadExplanation',
                'Your Google avatar is shown as a placeholder but cannot be used. Click "Upload Photo from your computer" above and choose a file from your computer — that\'s the only way to enable the Save button.'
              )}
            </Alert>
          )}

          {/* Name fields */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.firstName', 'First Name')}
                value={firstName}
                onChange={(e) => {
                  const val = e.target.value;
                  setFirstName(val);
                  const isLatin = !val || LATIN_NAME_REGEX.test(val.trim());
                  setErrors((prev) => ({
                    ...prev,
                    firstName: isLatin ? undefined : t('profile.latinOnly', 'Latin characters only'),
                  }));
                }}
                error={!!errors.firstName}
                helperText={errors.firstName || t('profile.latinOnly', 'Latin characters only')}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('profile.lastName', 'Last Name')}
                value={lastName}
                onChange={(e) => {
                  const val = e.target.value;
                  setLastName(val);
                  const isLatin = !val || LATIN_NAME_REGEX.test(val.trim());
                  setErrors((prev) => ({
                    ...prev,
                    lastName: isLatin ? undefined : t('profile.latinOnly', 'Latin characters only'),
                  }));
                }}
                error={!!errors.lastName}
                helperText={errors.lastName || t('profile.latinOnly', 'Latin characters only')}
                size="small"
              />
            </Grid>
          </Grid>

          {/* Phone fields */}
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label={t('profile.phone', 'Corporate Phone')}
                value={phone}
                onChange={(e) => {
                  // Strip every character that the regex doesn't accept so users
                  // can't accidentally paste in spaces, dashes or parentheses —
                  // those caused silent validation failures before the helper text
                  // was added.
                  const cleaned = e.target.value.replace(/[^\d+]/g, '');
                  setPhone(cleaned);
                  setErrors((prev) => ({ ...prev, phone: undefined }));
                }}
                error={!!errors.phone}
                helperText={
                  errors.phone ||
                  t('profile.phoneFormatHelper', 'International format only, no spaces or symbols. Example: +48739109524')
                }
                placeholder="+48739109524"
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label={t('profile.phoneExtension', 'Extension')}
                value={phoneExtension}
                onChange={(e) => {
                  // Allow only digits
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setPhoneExtension(val);
                  setErrors((prev) => ({ ...prev, phoneExtension: undefined }));
                }}
                error={!!errors.phoneExtension}
                helperText={errors.phoneExtension || t('profile.phoneExtensionHelper', 'IVR extension code (2-6 digits)')}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>

        {errors.submit && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {errors.submit}
            </Alert>
          </Box>
        )}

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!isPaywall && (
            <Button onClick={onClose}>
              {t('profile.cropCancel', 'Cancel')}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            // Keep the button clickable so users can see exactly why their form
            // is invalid (e.g. missing photo upload) instead of staring at a
            // dead Save button.
            disabled={saving}
          >
            {saving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('profile.saving', 'Saving...')}
              </>
            ) : (
              t('profile.save', 'Save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CropModal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        imageSrc={rawImage}
        onCropComplete={handleCropComplete}
      />
    </>
  );
};

export default ProfileSetupModal;
